import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { LuCircleX, LuMessageSquareText, LuPencil, LuSave, LuX } from 'react-icons/lu';
import Button from '../ui/Button';
import GlassIcon from '../ui/GlassIcon';
import Modal from '../ui/Modal';
import { Field, TextArea } from '../form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';

const ROSE = 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30';

const DialogIcon = ({ icon: Icon, gradient = ROSE }) => (
  <GlassIcon gradient={gradient} size="xs">
    <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
  </GlassIcon>
);

DialogIcon.propTypes = { icon: PropTypes.elementType.isRequired, gradient: PropTypes.string };

const useLabels = () => {
  const { t, language } = useTranslation();
  return { t, language, label: (key, vars) => t(`profilePage.expense.approvals.${key}`, vars) };
};

/** Reads a rejection reason in full. */
export const ReasonDialog = ({ isOpen, reason, onClose }) => {
  const { t, language, label } = useLabels();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={label('reasonDialog.title')}
      closeLabel={t('common.close')}
      lang={language}
      icon={<DialogIcon icon={LuMessageSquareText} />}
      footer={<Button onClick={onClose}>{label('reasonDialog.close')}</Button>}
    >
      <p className="tw-whitespace-pre-line tw-break-words tw-rounded-2xl tw-bg-rose-500/[0.07] tw-px-4 tw-py-3 tw-text-[15px] tw-leading-relaxed tw-text-slate-800 dark:tw-text-slate-100">
        {reason}
      </p>
    </Modal>
  );
};

ReasonDialog.propTypes = { isOpen: PropTypes.bool.isRequired, reason: PropTypes.string, onClose: PropTypes.func.isRequired };

/**
 * Text box for a rejection reason: `mode="reject"` rejects a pending claim,
 * `mode="edit"` changes the reason of a rejected one.
 */
export const ReasonFormDialog = ({ isOpen, mode, value, onChange, onCancel, onSubmit }) => {
  const { t, language, label } = useLabels();
  const reject = mode === 'reject';
  const prefix = reject ? 'rejectDialog' : 'editDialog';

  // Pencere odağı önce kapat düğmesine verir; yazı alanına hemen ardından geçilir
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => document.getElementById('expense-reason')?.focus(), 60);
    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      title={label(`${prefix}.title`)}
      closeLabel={t('common.close')}
      lang={language}
      icon={<DialogIcon icon={reject ? LuCircleX : LuPencil} gradient={reject ? ROSE : 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30'} />}
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel}>{label(`${prefix}.cancel`)}</Button>
          <Button variant={reject ? 'danger' : 'primary'} onClick={onSubmit} disabled={!value.trim()} className="max-sm:tw-flex-1">
            {reject ? <LuX aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {reject ? label('rejectDialog.confirm') : label('editDialog.save')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{label(`${prefix}.text`)}</p>
        <Field label={label(`${prefix}.label`)} htmlFor="expense-reason" required>
          <TextArea
            id="expense-reason"
            rows={5}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={label(`${prefix}.placeholder`)}
          />
        </Field>
      </div>
    </Modal>
  );
};

ReasonFormDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['reject', 'edit']).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
